import { useState, useEffect } from "react";
import { ChevronDown, Menu } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  getAdminAccessToken,
  getAdminRefreshToken,
  getAdminUserInfo,
  setAdminUserInfo,
  clearLocalStorage,
} from "@lib/storeManager";
import { adminAPI } from "@/project/jain_elibrary-admin/services/adminService";
import { paths } from "@/project/jain_elibrary-admin/utils/paths";
import { jwtDecode } from "jwt-decode";
import { getAdminPageTitle } from "@/project/jain_elibrary-admin/utils/adminPageTitles";

export const AdminHeader = ({ onMenuClick }: { onMenuClick?: () => void }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [userInfo, setUserInfo] = useState<{
    first_name?: string;
    last_name?: string;
    email?: string;
    user_type?: string;
  } | null>(null);

  useEffect(() => {
    const loadUserInfo = () => {
      try {
        // Jain admin login/profile use adminUserInfo + adminAccessToken (not tenant userInfo/accessToken).
        const admin = getAdminUserInfo();
        if (admin && (admin.first_name || admin.last_name || admin.email)) {
          setUserInfo(admin);
          return;
        }

        const token = getAdminAccessToken();
        if (token) {
          try {
            const decoded = jwtDecode<{
              first_name?: string;
              last_name?: string;
              email?: string;
              user_type?: string;
              user_id?: number;
            }>(token);
            const userFromToken = {
              first_name: decoded.first_name ?? "",
              last_name: decoded.last_name ?? "",
              email: decoded.email ?? "",
              user_type: decoded.user_type ?? "",
            };
            if (
              userFromToken.first_name ||
              userFromToken.last_name ||
              userFromToken.email
            ) {
              setUserInfo(userFromToken);
              setAdminUserInfo({ ...admin, ...userFromToken });
            }
          } catch (error) {
            console.error("Error decoding admin access token:", error);
          }
        }
      } catch (error) {
        console.error("Error loading admin user info:", error);
      }
    };

    loadUserInfo();

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "adminUserInfo" || e.key === "adminAccessToken") {
        loadUserInfo();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    const handleUserInfoUpdate = () => {
      loadUserInfo();
    };

    window.addEventListener("userInfoUpdated", handleUserInfoUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("userInfoUpdated", handleUserInfoUpdate);
    };
  }, []);

  // Generate user initials for avatar fallback
  const getInitials = () => {
    if (!userInfo) {
      return "U";
    }
    const first = (userInfo.first_name?.trim() ?? "").charAt(0).toUpperCase();
    const last = (userInfo.last_name?.trim() ?? "").charAt(0).toUpperCase();
    const initials = (first + last).trim();
    return initials ?? "U";
  };

  // Get full name
  const getFullName = () => {
    if (!userInfo) {
      return "User";
    }
    const firstName = (userInfo.first_name ?? "").trim();
    const lastName = (userInfo.last_name ?? "").trim();
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || "User";
  };

  const handleLogout = async () => {
    try {
      const refreshToken = getAdminRefreshToken();
      if (refreshToken) {
        try {
          await adminAPI.post("/auth/logout/", { refresh: refreshToken });
        } catch (error) {
          // Ignore logout API errors, continue with local cleanup
          console.error("Logout API error:", error);
        }
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear all local storage
      clearLocalStorage();
      setShowLogoutDialog(false);
      navigate(paths.login);
    }
  };

  return (
    <>
      <header className="sticky top-0 z-30 shrink-0 border-b border-border bg-card">
        <div className="mx-auto flex h-14 w-full max-w-screen-2xl items-center justify-between gap-3 px-4 sm:h-16 sm:px-6 lg:px-8 2xl:px-10">
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0 lg:hidden"
              aria-label="Open navigation menu"
              onClick={onMenuClick}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <h1 className="truncate text-lg font-bold text-foreground sm:text-2xl">
              {getAdminPageTitle(location.pathname)}
            </h1>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger className="flex max-w-[min(12rem,45vw)] shrink-0 items-center gap-2 transition-opacity hover:opacity-80 sm:max-w-none sm:gap-3">
              <Avatar className="h-8 w-8 sm:h-10 sm:w-10">
                <AvatarImage src="/placeholder.svg" />
                <AvatarFallback className="text-xs sm:text-sm">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
              <span className="hidden truncate font-medium sm:inline">
                {getFullName()}
              </span>
              <ChevronDown className="hidden h-4 w-4 sm:block" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="z-50 w-48 border border-border bg-card"
            >
              <DropdownMenuItem
                onClick={() => navigate(paths.profile)}
                className="cursor-pointer"
              >
                User Profile
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate(paths.changePassword)}
                className="cursor-pointer"
              >
                Change Password
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setShowLogoutDialog(true)}
                className="cursor-pointer"
              >
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <Dialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-lg">
              Are you sure, do you want to logout?
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 flex justify-center gap-4">
            <Button
              onClick={handleLogout}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Logout
            </Button>
            <Button
              onClick={() => setShowLogoutDialog(false)}
              variant="outline"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
